import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase/firebaseConfig';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { ref, set, get, update, increment } from 'firebase/database';
import { generateId } from '../firebase/idGenerator';
import { createNotification, sendTemplateNotification } from '../services/notificationService';

function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true); // Start loading to check settings
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkSettings = async () => {
      try {
        const snap = await get(ref(db, 'settings/registrationEnabled'));
        if (snap.exists()) {
          setRegistrationEnabled(snap.val());
        }
      } catch (e) {
        console.error("Could not fetch registration settings", e);
      } finally {
        setLoading(false);
      }
    };
    checkSettings();
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      return setError("Password must be at least 6 characters.");
    }
    setError("");
    setLoading(true);

    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      const rawPid = await generateId('PID', 'users', 'profileId');
      const profileId = `DEV-${rawPid.split('-')[2]}`;
      const suid = await generateId('SUID', 'users', 'suid');
      const eid = await generateId('EID', 'users', 'eid');

      // Store in realtime database "users"
      await set(ref(db, "users/" + user.uid), {
        uid: user.uid,
        suid: suid,
        eid: eid,
        profileId: profileId,
        name: name,
        email: email,
        githubUsername: "",
        leetcodeUsername: "",
        codeforcesUsername: "",
        gfgUsername: "",
        bio: "",
        role: "user",
        status: "active",
        points: 100, // Genesis Bonus
        referrals: {
          count: 0,
          milestones: { m1: false, m3: false, m5: false },
          list: []
        },
        createdAt: new Date().toISOString()
      });

      // 1. Create Welcome Notification (Template Based - Persistent)
      await sendTemplateNotification(user.uid, 'ACCOUNT_CREATED', 876600);

      // 2. PROCESS REFERRAL IF EXISTS
      const searchParams = new URLSearchParams(location.search);
      const referrerUid = searchParams.get('ref');
      
      if (referrerUid && referrerUid !== user.uid) {
        try {
          const referrerRef = ref(db, `users/${referrerUid}`);
          const referrerSnap = await get(referrerRef);
          
          if (referrerSnap.exists()) {
            const rData = referrerSnap.val();
            const newCount = (rData.referrals?.count || 0) + 1;
            
            let bonus = 20; // Standard reward
            let milestoneMsg = "";
            
            // Milestone Bonuses
            if (newCount === 1 && !rData.referrals?.milestones?.m1) {
              bonus += 50;
              milestoneMsg = " (incl. +50 Milestone Bonus!)";
            } else if (newCount === 3 && !rData.referrals?.milestones?.m3) {
              bonus += 100;
              milestoneMsg = " (incl. +100 Milestone Bonus!)";
            } else if (newCount === 5 && !rData.referrals?.milestones?.m5) {
              bonus += 250;
              milestoneMsg = " (incl. +250 Milestone Bonus!)";
            }

            // Update Referrer
            await update(ref(db, `users/${referrerUid}`), {
              points: increment(bonus),
              'referrals/count': increment(1),
              [`referrals/milestones/m${newCount}`]: true,
              [`referrals/list/${user.uid}`]: {
                name: name,
                date: new Date().toISOString()
              }
            });

            // Notify Referrer
            await createNotification(referrerUid, {
              title: "🌟 Referral Success!",
              message: `Your invitee ${name} just joined DevTracker. You've earned ${bonus} AI Credits${milestoneMsg}.`,
              type: "success"
            });
          }
        } catch (err) {
          console.error("Referral processing error", err);
        }
      }

      navigate("/dashboard");
    } catch (err) {
      setError("Failed to create an account. Email may already be in use.");
      console.error(err);
    }
    setLoading(false);
  };
  if (loading) return <div className="page-container"><div className="loader" style={{margin:'100px auto'}}></div></div>;

  if (!registrationEnabled) {
    return (
      <div className="container animate-up" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh'}}>
        <div className="glass-card" style={{ width: '100%', maxWidth: '450px', padding: '40px', textAlign: 'center' }}>
          <i className="fa-solid fa-lock" style={{fontSize: '3rem', color: '#ff375f', marginBottom: '20px'}}></i>
          <h2 style={{marginBottom: '15px'}}>Registration Closed</h2>
          <p style={{color: 'var(--text-secondary)', marginBottom: '30px'}}>
            DevTracker is currently operating in an invite-only phase. Public registration has been temporarily suspended by the platform administrators.
          </p>
          <Link to="/" className="btn btn-secondary">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container animate-up" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh'}}>
      <div className="glass-card" style={{ width: '100%', maxWidth: '450px', padding: '40px' }}>
        <h2 style={{textAlign: 'center', marginBottom: '30px', color: 'var(--brand-accent)'}}>Create Profile</h2>
        
        {error && <div className="error-text" style={{background: 'rgba(255, 107, 107, 0.1)', padding: '10px', borderRadius: '8px', marginBottom: '20px', textAlign: 'center'}}>{error}</div>}
        
        <form onSubmit={handleRegister}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input 
              type="text" 
              className="form-input" 
              required
              value={name}
              autoComplete="name"
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input 
              type="email" 
              className="form-input" 
              required
              value={email}
              autoComplete="email"
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input 
              type="password" 
              className="form-input" 
              required
              value={password}
              autoComplete="new-password"
              onChange={e => setPassword(e.target.value)}
            />
          </div>
          <button disabled={loading} type="submit" className="btn btn-primary" style={{width: '100%', marginTop: '15px'}}>
            {loading ? 'Creating...' : 'Sign Up'}
          </button>
        </form>
        
        <div style={{marginTop: '25px', textAlign: 'center', fontSize: '0.95rem', color: 'var(--text-secondary)'}}>
          Already have an account? <Link to="/login" style={{color: 'var(--brand-accent)'}}>Log In</Link>
        </div>
      </div>
    </div>
  );
}

export default Register;
